import React from "react";
import {
    CloseIcon,
    DocumentationIcon,
    DeleteIconsOptions,
    DownloadIcon,
    IconDocumentLinesCircle,
    IconVehicle
} from "../../../../../components/buttons/IconButtons";
import {CustomTextField} from "../../../../../components/CustomTextField";
import {CustomSelect} from "../../../../../components/CustomSelect";
import CustomDateTimePicker from "../../../../../components/CustomDateTimePicker";
import CustomPhotoPickerMultiple from "../../../../../components/PhotoPicker/CustomPhotoPickerMultiple";
import CustomUserChip from "../../../../../components/CustomUserChip";
import {FILE_TYPE} from "../../../../../Helpers/fileType";
import {fileSize} from "../../../../../constant/variable";
import {POSITION_MODEL} from "../../../../../Helpers/PositionModel";
import DepartureSection from "../EntryAndExitForms/PlantMaterialControl/DepartureSection";
import MovementDirectionSection from "../EntryAndExitForms/PlantMaterialControl/MovementDirectionSection";
import GateLogCard from "../EntryAndExitForms/Details/Sections/GateLogCard";
import {
    getMovementDirectionOption,
    getMovementDirectionSection,
    getTransportFieldLength,
    VEHICLE_MOVEMENT_DIRECTION_OPTIONS,
    VEHICLE_MOVEMENT_REASONS
} from "../../Model/Model";
import VehicleMovementSectionsApp from "../../Functionality/VehicleMovements/VehicleMovementSectionsApp";
import {GATE_LOG_NOTES_LENGTH} from "../../../Guard/MaterialControl/EntryAndExitForms/Details/Model/Model";
import { PERIOD_TIME_UNIT_ENUM } from "../../../../../enums/PeriodTimeUnitEnum";

/**
 * Fields of the vehicle that makes the movement.
 *
 * None of them is required, because the vehicle is read at the gate: what the
 * requester leaves blank is completed by the guard when the vehicle arrives.
 */
const VEHICLE_FIELDS = [
    {
        id: 'txt_vms_company_or_area',
        name: 'companyOrArea',
        trans: 'company_or_area',
        placeholder: 'enter_the_company_or_internal_area',
        className: 'col-12'
    },
    {
        id: 'txt_vms_plate_no',
        name: 'plateNo',
        trans: 'plate_no',
        placeholder: 'enter_plate_or_unit_number',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_vms_vehicle_model',
        name: 'vehicleModel',
        trans: 'vehicle_model',
        placeholder: 'enter_vehicle_make_and_model',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_vms_driver_s_name',
        name: 'driversName',
        trans: 'driver_s_name',
        placeholder: 'enter_driver_s_name',
        className: 'col-sm-12 col-md-6'
    },
    {
        id: 'txt_vms_license_no',
        name: 'licenseNo',
        trans: 'license_no',
        placeholder: 'enter_license_number',
        className: 'col-sm-12 col-md-6'
    }
];

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Sections of a movement of a vehicle: the movement itself, the documentation
 * that supports it and the values the guard completes at the gate.
 */
const VehicleMovementSections = ({
    emptyFields,
    materialRequest,
    setMaterialRequest,
    photoPicker,
    setPhotoPicker,
    setEmptyFieldsItems
}) => {

    const {
        t,
        handleChange,
        handleSelectMovementDirection,
        showGateMessage,
        setShowGateMessage
    } = VehicleMovementSectionsApp({
        materialRequest,
        setMaterialRequest,
        setEmptyFieldsItems
    });

    // The movement is captured with the same reasons the movements of the
    // vehicles are read with in their table
    const movementTypes = Object.values(VEHICLE_MOVEMENT_REASONS)
        .map((e) => ({id: e.key, name: t(e.trans)}));

    const selectedDirectionOption = getMovementDirectionOption(materialRequest?.movementDirection);
    // The return of the vehicle is the movement opposite to the one it takes, so
    // the section follows the direction the movement was registered with
    const selectedDirectionSection = getMovementDirectionSection(materialRequest?.movementDirection);
    const SelectedDirectionIcon = selectedDirectionOption?.Icon;

    return (
        <>
            <MovementDirectionSection
                t={t}
                idPrefix={"vms"}
                materialRequest={materialRequest}
                onSelectDirection={handleSelectMovementDirection}
                emptyFields={emptyFields}
                options={VEHICLE_MOVEMENT_DIRECTION_OPTIONS}
                headerSection={
                    <div className={"row"}>
                        <div className={"col-12 px-1 mt-3"}>
                            <CustomSelect
                                id={'slt_vms_movement_type'}
                                name={'movementType'}
                                trans={'movement_type'}
                                list={movementTypes}
                                keyName={'id'}
                                value={materialRequest?.movementType ?? ''}
                                onChange={handleChange}
                                required={true}
                                showNoSelectionOption={false}
                                emptyFields={emptyFields}
                            />
                        </div>
                        <div className='col-12 px-1'>
                            <CustomTextField
                                length={GATE_LOG_NOTES_LENGTH}
                                id='txt_vms_extra_details'
                                name='extraDetails'
                                value={materialRequest?.extraDetails ?? ''}
                                trans='extra_details'
                                emptyFields={false}
                                required={false}
                                onChange={handleChange}
                                multiline={true}
                                rows={4}
                                placeholder={t('add_anything_the_guard_should_know_about_this_movement')}
                            />
                        </div>
                    </div>
                }
            >
                { selectedDirectionSection &&
                <DepartureSection
                    t={t}
                    section={selectedDirectionSection}
                    materialRequest={materialRequest}
                    handleChange={handleChange}
                    emptyFields={emptyFields}
                    showCheckbox={false}
                />
                }
            </MovementDirectionSection>

            <div className={'mt-3'}>
                <GateLogCard t={t} Icon={DocumentationIcon} title={'documentation'}>
                    <div className={"row p-3 mb-60px"}>
                        <p className={"fs-14px ibl-font-w-600 mb-3"}>{t("supporting_documents")} *</p>
                        <div className={'col-12'}>
                            <CustomPhotoPickerMultiple
                                id='fu_vms_file'
                                photoPickerFile={photoPicker}
                                setPhotoPickerFile={setPhotoPicker}
                                accept={{ ...FILE_TYPE.IMAGE_FILES, size: fileSize }}
                                showBtnDeleteItemGallery={true}
                                showBtnCamera={false}
                                deleteIcon={DeleteIconsOptions}
                                downloadIcon={DownloadIcon}
                                showBtnDownload={true}
                                apiDelete={false}
                                disabled={false}
                                required={true}
                                emptyFields={emptyFields && photoPicker?.listFiles?.length === 0}
                            />
                        </div>
                    </div>
                </GateLogCard>
            </div>

            <div className='col-12 py-3 mt-3 border-radius-8px background-secondary-50 border-dashed-secondary-150'>
                { showGateMessage &&
                    <CustomUserChip
                        className='m-0 p-0 chip-secondary-150-100 justify-content-start d-flex w-100'
                        name={t('any_field_you_leave_blank_will_be_completed_by_the_guard_at_the_gate')}
                        Icon={IconDocumentLinesCircle}
                        showAvatar={true}
                        classNameIcon={'icon-color-secondary-700'}
                        variant='large'
                        iconPosition={POSITION_MODEL.LEFT}
                        onDelete={() => setShowGateMessage(false)}
                        deleteIcon={<CloseIcon className={'font-color-secondary-700 h-18px'}/>}
                    />
                }

                <div className={"mt-3"}>
                    <GateLogCard t={t} Icon={IconVehicle} title={'transport'}>
                        <div className={"row pt-3"}>
                            {VEHICLE_FIELDS.map(({id, name, trans, placeholder, className}) =>
                                <div className={className} key={name}>
                                    <CustomTextField
                                        id={id}
                                        length={getTransportFieldLength(name)}
                                        name={name}
                                        value={materialRequest?.[name] ?? ''}
                                        trans={trans}
                                        type={'text'}
                                        onChange={handleChange}
                                        emptyFields={false}
                                        required={false}
                                        placeholder={t(placeholder)}
                                    />
                                </div>
                            )}
                        </div>
                    </GateLogCard>
                </div>

                { selectedDirectionOption &&
                <div className={"mt-3"}>
                    <GateLogCard
                        t={t}
                        Icon={SelectedDirectionIcon}
                        iconBackground={selectedDirectionOption.iconBackground}
                        iconColor={selectedDirectionOption.iconColor}
                        title={selectedDirectionOption.trans}
                    >
                        <div className={"row pt-3"}>
                            <div className={"col-12"}>
                                <CustomDateTimePicker
                                    id={`dp_vms_${selectedDirectionOption.dateName}`}
                                    name={selectedDirectionOption.dateName}
                                    onChange={(e) => {
                                        handleChange({target: {value: e, name: selectedDirectionOption.dateName}});
                                    }}
                                    trans={selectedDirectionOption.dateTrans}
                                    value={materialRequest?.[selectedDirectionOption.dateName]}
                                    required={false}
                                    openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                                    disablePast={true}
                                    emptyFields={false}
                                />
                            </div>
                            <div className={"col-12"}>
                                <CustomTextField
                                    id={'txt_vms_notes'}
                                    length={GATE_LOG_NOTES_LENGTH}
                                    name={'notes'}
                                    value={materialRequest?.notes ?? ''}
                                    trans={'notes'}
                                    type={'text'}
                                    multiline={true}
                                    rows={4}
                                    onChange={handleChange}
                                    emptyFields={false}
                                    required={false}
                                    placeholder={t('add_any_relevant_notes_or_observations')}
                                />
                            </div>
                        </div>
                    </GateLogCard>
                </div>
                }
            </div>
        </>
    );
};

export default VehicleMovementSections;
